import { toJs } from "@Sk";
import { ConsoleOutputDTO, DesignerRpcFunctions, IdeRpcCaller, IdeRpcRaiser, MaybePromise } from "./shared-types";

const functions: { [fnName: string]: Function } = {};

export const registerFn = <FnName extends keyof DesignerRpcFunctions>(
    name: FnName,
    fn: MaybePromise<DesignerRpcFunctions[FnName]>
) => {
    functions[name] = fn;
};

export const send = (msg: any, targetOrigin = "*") => {
    //console.log("Runtime client sent", msg);
    if (window.parent !== window) {
        window.parent.postMessage(msg, targetOrigin);
    } else if (window.opener) {
        window.opener.postMessage(msg, targetOrigin);
    }
};

const defer = () => {
    let deferObj: any = {};
    deferObj.promise = new Promise((resolve, reject) => {
        deferObj.resolve = resolve;
        deferObj.reject = reject;
    });
    return deferObj;
};

const outstandingRequests: { [requestId: number]: any } = {};
let nextRequestId = 1;

export const call: IdeRpcCaller = (fn: string, ...args: any[]): any => {
    const requestId = nextRequestId++;
    outstandingRequests[requestId] = defer();
    send({ requestId, fn, args });
    return outstandingRequests[requestId].promise;
};

export const raiseEvent: IdeRpcRaiser = (fn: string, ...args: any[]): void => {
    send({ fn, args });
};

const listener = async (e: MessageEvent) => {
    // Filter out messages without data.
    if (!e.data) {
        return;
    }

    // Check source origin of incoming message. Make sure it's the Anvil IDE.
    if (e.origin != window.anvilParams.ideOrigin) {
        console.warn("Ignoring message from invalid origin:", e.origin);
        return;
    }

    //console.log("Runtime client got message: ", e);

    const { requestId, fn, args, result, error } = e.data;
    if (fn) {
        // This is an incoming call
        const func = functions[fn];
        var rv: any;
        try {
            if (func) {
                rv = { result: await func(...args) };
            } else {
                console.debug("Message to designer not recognised:", e.data);
                rv = { error: "RPC function '" + fn + "' not recognised in designer" };
            }
        } catch (err: any) {
            if (err instanceof Sk.builtin.ExternalError) {
                console.error(err, err.nativeError);
            } else {
                console.error(err, err.stack || "(no stack trace)");
            }
            const errDTO: ConsoleOutputDTO =
                err instanceof Sk.builtin.BaseException
                    ? errorForRpc(err)
                    : {
                          fn: "runtimeError",
                          error: "" + err,
                          msg: "" + err,
                          stackTrace: err?.stack,
                      };
            raiseEvent("consoleOutput", errDTO);
            rv = { error: "" + err };
        }

        if (requestId) {
            // Response only required if there was a requestId
            rv.requestId = requestId;
            send(rv, e.origin);
        }
    } else {
        // This is an incoming response
        const { resolve, reject } = outstandingRequests[requestId] || {};
        if (!resolve) {
            // Request not found
            console.warn("Received response to unknown RPC request:", e.data);
        } else {
            delete outstandingRequests[requestId];
            if ("result" in e.data) {
                resolve(result);
            } else if ("error" in e.data) {
                reject(error);
            } else {
                reject(`Malformed RPC response: ${JSON.stringify(e.data)}`);
            }
        }
    }
};

export const initialise = () => {
    window.addEventListener("message", listener);
    raiseEvent("ready");
};

export const errorForRpc = (errorObj: any /*BaseException*/) => {
    // Python error
    const errorMsg = {
        fn: "pythonError",
        traceback: errorObj.traceback,
        type: errorObj.tp$name,
        msg: toJs(errorObj.args as string[])[0],
        errorObj: undefined,
    };

    if (errorObj._anvil && errorObj._anvil.errorObj) {
        errorMsg.type = errorObj._anvil.errorObj.type;
        errorMsg.errorObj = errorObj._anvil.errorObj;
    }
    return errorMsg as ConsoleOutputDTO;
};
